import { ReactNode, useState } from 'react';

import { ClientJoin } from '../../components/join-meet/client';
import { PsychologJoin } from '../../components/join-meet/psycholog';
import { GroupClientLobby } from '../../components/meet/group-client-lobby';
import { getConsultationClientName, getGroupDisplayName } from '../../features/meet/meet.adapters';
import { useMeetModeration } from '../../features/meet/MeetModerationProvider';
import { useMeetOverlays } from '../../features/meet/MeetOverlaysProvider';
import { IClient, IConsultation } from '../../store/types';
import { NetworkCheckState } from '../../hooks/useVpnDetection';
import { MeetSupportChat } from '../../features/support/MeetSupportChat';
import { useSupportAiBotFeatureFlag } from '../../features/support/useSupportAiBotFeatureFlag';

interface MeetJoinFlowProps {
  consultation: IConsultation;
  client?: IClient;
  users: unknown[];
  isPsychologist: boolean;
  isGroupConsultationFlag: boolean;
  videoRoomInitialized: boolean;
  settingsModal: ReactNode;
  startWithMedia: (value: boolean, isCameraEnabled?: boolean) => void | Promise<void>;
  onOpenSettingsChange: (value: boolean) => void | Promise<void>;
  livekitToken: string | null;
  networkCheckState: NetworkCheckState;
}

export function MeetJoinFlow({
  consultation,
  client,
  users,
  isPsychologist,
  isGroupConsultationFlag,
  videoRoomInitialized,
  settingsModal,
  startWithMedia,
  onOpenSettingsChange,
  livekitToken,
  networkCheckState,
}: MeetJoinFlowProps) {
  const overlays = useMeetOverlays();
  const moderation = useMeetModeration();
  const { enabled: isSupportAiBotEnabled } = useSupportAiBotFeatureFlag();
  const [preferredCameraEnabled, setPreferredCameraEnabled] = useState(true);
  const consultationClientName = getConsultationClientName(consultation);
  const groupDisplayName = isGroupConsultationFlag
    ? getGroupDisplayName(client?.full_name, consultationClientName)
    : undefined;

  if (isGroupConsultationFlag && !isPsychologist && moderation.groupPermissionRequested) {
    return (
      <GroupClientLobby
        connectionAllowed={moderation.connectionAllowedForGroupClient}
        initialCameraEnabled={preferredCameraEnabled}
        onEnterMeeting={(isCameraEnabled) => startWithMedia(true, isCameraEnabled)}
      />
    );
  }

  return (
    <div className="w-full h-full">
      {isPsychologist ? (
        <PsychologJoin
          setOpenSettings={onOpenSettingsChange}
          setStarted={startWithMedia}
          consultation={consultation}
          client={client}
          users={users}
          disabled={!videoRoomInitialized}
          isRoomInitialized={videoRoomInitialized}
          isGroupConsultation={isGroupConsultationFlag}
          livekitToken={livekitToken}
          networkCheckState={networkCheckState}
        />
      ) : (
        <ClientJoin
          consultation={consultation}
          groupName={groupDisplayName}
          setStarted={startWithMedia}
          onRequestPermission={
            isGroupConsultationFlag
              ? (name, isCameraEnabled) => {
                  setPreferredCameraEnabled(isCameraEnabled);
                  moderation.requestGroupPermission(name);
                }
              : undefined
          }
          setOpenSettings={onOpenSettingsChange}
          users={users}
          disabled={!videoRoomInitialized}
          isRoomInitialized={videoRoomInitialized}
          isGroupConsultation={isGroupConsultationFlag}
          livekitToken={livekitToken}
          networkCheckState={networkCheckState}
        />
      )}
      {overlays.openSettings && settingsModal}
      {isSupportAiBotEnabled ? (
        <MeetSupportChat
          source="video-prejoin"
          roomName={consultation?.code || consultation?.id?.toString()}
          roomId={consultation?.id != null ? String(consultation.id) : undefined}
          connected={false}
          onRetryConnection={() => startWithMedia(true, preferredCameraEnabled)}
        />
      ) : null}
    </div>
  );
}
